import React from 'react';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import Icon, {Icons} from '@pb/kit-component-icon';
import Loading from '@pb/kit-component-loading';
import {Tooltip} from '@pb/kit-component-popups';

import {
    checkingIndicator,
    domainListActions,
    domainListItem,
    domainListItemDeselected,
    domainListItemUnverified,
    domainName,
    domainNameRow,
    removeButton,
    verifiedIndicator,
    warningIcon,
    warningTooltip,
} from '../domain-manager.module.styl';

import type {DomainEntry} from '../../../utils/domain-validation';

type DomainListItemProps = {
    domain: DomainEntry,
    checking?: boolean,
    disabled?: boolean,
    selectable?: boolean,
    selected?: boolean,
    onToggleSelect?: () => void,
    onRemove: () => void,
    onVerify: () => void,
};

export const DomainListItem: React.FC<DomainListItemProps> = ({
    domain,
    checking = false,
    disabled = false,
    selectable = false,
    selected = true,
    onToggleSelect,
    onRemove,
    onVerify,
}) => {
    const hasWarning = domain.warning != null && domain.warning.length > 0;
    const className = [
        domainListItem,
        !domain.verified ? domainListItemUnverified : '',
        !selected ? domainListItemDeselected : '',
    ].join(' ');

    let status: React.ReactNode;
    if (checking) {
        status = (
            <div className={checkingIndicator}>
                <Loading type="ICON"/>
                Checking...
            </div>
        );
    } else if (domain.verified) {
        status = (
            <div className={verifiedIndicator}>
                {hasWarning ? (
                    <Tooltip
                        color="light"
                        placement="top-start"
                        anchor={({anchorRef, getAnchorProps}) => (
                            <span ref={anchorRef} {...getAnchorProps()} className={warningIcon}>
                                <Icon type={Icons.WARNING} size="Size_14"/>
                            </span>
                        )}>
                        <div className={warningTooltip}>{domain.warning}</div>
                    </Tooltip>
                ) : (
                    <Icon type={Icons.CHECK} size="Size_14"/>
                )}
                Verified
            </div>
        );
    } else {
        status = (
            <Button variant="Type.SECONDARY" onClick={onVerify} disabled={disabled}>
                Verify
            </Button>
        );
    }

    return (
        <div className={className}>
            {selectable ? (
                <CheckBox
                    checked={selected}
                    onCheckBoxChange={() => onToggleSelect?.()}
                    usePropsOnly={true}
                />
            ) : null}
            <div className={domainNameRow}>
                <span className={domainName}>{domain.value}</span>
            </div>
            <div className={domainListActions}>
                {status}
                <span
                    className={removeButton}
                    role="button"
                    tabIndex={0}
                    onClick={onRemove}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            onRemove();
                        }
                    }}>
                    <Icon type={Icons.CROSS} size="Size_14"/>
                </span>
            </div>
        </div>
    );
};
